'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowLeft, ArrowRight, Phone } from 'lucide-react'
import { useRef } from 'react'
import type { VeicoloDataVeicolo } from '@/interface/VeicoloDataVeicolo'
import { canOptimizeImageSource, getDisplayImageSource } from '@/lib/image-source'
import { getHomepageMediaAlt, getHomepageMediaUrl, type HomepageContent } from './PayloadHomepageHero'

function formatVehiclePrice(value: string | number | undefined) {
  const rawValue = String(value ?? '').trim()
  const amount = Number(rawValue)
  if (!rawValue || !Number.isFinite(amount)) return 'Prezzo su richiesta'
  return `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 0 }).format(amount)} €`
}

function CommercialVehicleCard({
  vehicle,
  vatLabel,
  buttonLabel,
  fallbackHref,
}: {
  vehicle: VeicoloDataVeicolo
  vatLabel: string
  buttonLabel: string
  fallbackHref: string
}) {
  const image = getDisplayImageSource(vehicle.foto, '/autoinarrivo.webp', { slug: vehicle.slug, articolo: vehicle.articolo })
  const optimize = canOptimizeImageSource(image)

  return (
    <article className="flex w-[260px] shrink-0 snap-start flex-col overflow-hidden rounded-[18px] border border-[#dce4ee] bg-white md:w-[300px] md:rounded-[20px]">
      <div className="relative h-[160px] shrink-0 bg-[#f3f6fa] md:h-[190px]">
        <Image src={image} alt={`${vehicle.marca} ${vehicle.modello}`} fill unoptimized={!optimize} sizes="(max-width: 767px) 260px, 300px" className="object-cover" />
      </div>
      <div className="flex min-h-[174px] flex-1 flex-col gap-2 px-[18px] pb-[18px] pt-4 md:min-h-[198px] md:gap-[10px] md:px-[22px] md:pb-[22px] md:pt-5">
        <p className="truncate text-xs font-semibold text-[#4a586f] md:text-[13px]">{vehicle.marca}</p>
        <h3 className="line-clamp-2 min-h-[42px] text-[16px] font-extrabold leading-[21px] text-[#10223a] md:text-[17px] md:leading-[22px]">{vehicle.modello}</h3>
        <div className="flex items-end justify-between gap-3 whitespace-nowrap">
          <p className="text-[24px] font-extrabold text-[#10223a] md:text-[26px]">{formatVehiclePrice(vehicle.prezzo)}</p>
          <p className="pb-1 text-[13px] font-bold text-[#005aa9] md:text-sm">{vatLabel}</p>
        </div>
        <Link href={vehicle.slug ? `/parco-veicoli/${vehicle.slug}` : fallbackHref} className="mt-auto flex h-[42px] items-center justify-center rounded-full bg-[#005aa9] text-sm font-bold text-white transition-colors hover:bg-[#004b8e] md:h-[46px]">
          {buttonLabel}
        </Link>
      </div>
    </article>
  )
}

export default function PayloadCommercialVehiclesSection({
  content,
  vehicles,
}: {
  content: HomepageContent
  vehicles: VeicoloDataVeicolo[]
}) {
  const section = content.commercialVehiclesSection
  const desktopImage = getHomepageMediaUrl(section?.desktopImage, 'original')
  const mobileImage = getHomepageMediaUrl(section?.mobileImage, 'original') || desktopImage
  const track = useRef<HTMLDivElement>(null)

  if (!section?.desktopImage || !desktopImage) return null

  const scroll = (direction: -1 | 1) => track.current?.scrollBy({ left: direction * 320, behavior: 'smooth' })

  return (
    <section className="bg-white pb-16 md:pb-[120px]" aria-labelledby="homepage-commercial-title">
      <div className="relative h-[560px] overflow-hidden bg-[#10223a] lg:h-[640px]">
        {mobileImage && <div className="absolute inset-x-0 top-0 h-[300px] overflow-hidden lg:hidden"><Image src={mobileImage} alt={getHomepageMediaAlt(section.mobileImage) || getHomepageMediaAlt(section.desktopImage)} fill priority unoptimized sizes="100vw" className="object-cover" /></div>}
        <Image src={desktopImage} alt={getHomepageMediaAlt(section.desktopImage)} fill priority unoptimized sizes="100vw" className="hidden object-cover lg:block" />
        <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(180deg,rgba(16,34,58,0)_30%,rgba(16,34,58,0.85)_55%,#10223a_100%)] lg:bg-[linear-gradient(90deg,rgba(16,34,58,0.92)_0%,rgba(16,34,58,0.78)_36%,rgba(16,34,58,0.12)_62%,rgba(16,34,58,0)_75%)]" />
        <div className="absolute inset-x-0 top-[242px] lg:top-[141px]">
          <div className="homepage-content-container">
            <div className="flex max-w-[590px] flex-col items-start gap-3 text-white lg:gap-5">
              {section.eyebrow && <p className="text-[15px] font-semibold text-white/85 lg:text-lg">{section.eyebrow}</p>}
              {section.title && <h2 id="homepage-commercial-title" className="max-w-[560px] text-[32px] font-extrabold leading-[1.04] tracking-[-0.576px] lg:text-[60px] lg:leading-none lg:tracking-[-1.8px]">{section.title}</h2>}
              {section.description && <p className="text-base text-white/90 lg:text-xl">{section.description}</p>}
              <div className="flex w-full flex-col gap-3 pt-1.5 lg:w-auto lg:flex-row lg:gap-[14px] lg:pt-2">
                {section.primaryLabel && section.primaryHref && <Link href={section.primaryHref} className="flex min-h-[50px] items-center justify-center rounded-full bg-white px-6 text-[15px] font-bold text-[#005aa9] lg:min-h-[56px] lg:text-base">{section.primaryLabel}</Link>}
                {section.contactLabel && section.contactHref && <Link href={section.contactHref} className="flex min-h-[50px] items-center justify-center gap-2 rounded-full border-[1.5px] border-white/70 px-6 text-[15px] font-semibold text-white lg:min-h-[56px] lg:text-base"><Phone aria-hidden="true" size={16} />{section.contactLabel}</Link>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-6 md:pt-[72px]">
        <div className="homepage-content-container mb-6 flex items-center justify-between gap-4 md:mb-7">
          <h2 className="text-[22px] font-extrabold tracking-[-0.22px] text-[#10223a] md:text-[32px] md:tracking-[-0.48px]">{section.catalogTitle}</h2>
          <div className="hidden shrink-0 gap-2.5 md:flex">
            <button type="button" aria-label="Veicolo commerciale precedente" onClick={() => scroll(-1)} className="flex h-[52px] w-[52px] items-center justify-center rounded-full border-[1.5px] border-[#005aa9] text-[#005aa9]"><ArrowLeft size={20} /></button>
            <button type="button" aria-label="Veicolo commerciale successivo" onClick={() => scroll(1)} className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-[#005aa9] text-white"><ArrowRight size={20} /></button>
          </div>
        </div>
        {vehicles.length > 0 ? (
          <div className="homepage-content-container">
            <div ref={track} className="homepage-edge-track flex snap-x snap-mandatory gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:gap-5">
              {vehicles.slice(0, 8).map((vehicle) => <CommercialVehicleCard key={vehicle.articolo || vehicle.slug || vehicle.productId} vehicle={vehicle} vatLabel={section.vatLabel || '+ IVA'} buttonLabel={section.vehicleButtonLabel || 'Scopri il veicolo'} fallbackHref={section.catalogHref || '/parco-veicoli?tipoVeicolo=Furgone'} />)}
            </div>
          </div>
        ) : <p className="homepage-content-container text-sm text-[#5b6b82]">Nessun veicolo commerciale disponibile al momento.</p>}
      </div>
    </section>
  )
}
